import { CommonModule } from '@angular/common';
import { Component, OnInit, Input, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { GigService } from '../../core/gig.service';
import { JobService } from '../../core/job.service';
import { VolService } from '../../core/vol.service';
import { AdService } from '../../core/ad.service';
import { environment } from '../../../environments/environment';
import { globals } from '../../../environments/globals';
import { Gig } from '../../models/gig.interface';
import { Job } from '../../models/job.interface';
import { Vol } from '../../models/vol.interface';
import { Ad } from '../../models/ad.interface';
import { GigCardComponent } from '../gig-card/gig-card';
import { JobCardComponent } from '../job-card/job-card';
import { VolCardComponent } from '../vol-card/vol-card';
import { AdCardComponent } from '../ad-card/ad-card';
import { GeCardComponent } from '../ge-card/ge-card';

@Component({
    imports: [AdCardComponent, CommonModule, GeCardComponent, GigCardComponent, JobCardComponent, VolCardComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    selector: 'ge-ads',
    templateUrl: './ge-ads.html',
    standalone: true
})
export class GeAdsComponent implements OnInit {
  appURL: string = environment.appURL;
  @Input() classification: string = '';
  @Input() alternate_classification: string = '';
  @Input() extra_classification: string = '';
  @Input() grouping: string = '';
  @Input() alternate_grouping: string = '';
  @Input() extra_grouping: string = '';
  @Input() skill: string = '';
  @Input() hidegigs: boolean = false;
  @Input() hidejobs: boolean = false;
  @Input() hidevols: boolean = false;
  @Input() hideads: boolean = false;
  @Input() hidegeads: boolean = false;
  @Input() hideremote: boolean = false;
  @Input() showgigs: boolean = false;
  @Input() showjobs: boolean = false;
  @Input() showvols: boolean = false;
  @Input() showads: boolean = false;
  @Input() showgeads: boolean = false;
  @Input() showremote: boolean = false;
  @Input() fetch: number = 5;
  @Input() offset: number = 1;
  @Input() view: string = 'list';

  slug: string | null = 'global';
  category: string = '';
  loaded: boolean = false;
  animate: boolean = false;
  count: number = 0;
  placeholder: boolean = false;
  scroll: boolean = true;
  match: boolean = false;
  country: any;
  countries: any[] = globals.countries.filter((site: { active: boolean; }) => site.active);
  gigtown: boolean = false;
  gigsintown: any;
  gegigs: any;
  jobsintown: any;
  gejobs: any;
  volsintown: any;
  gevols: any;
  adsintown: any;
  geads: any;
  geskills: any;
  genskills: any;
  gnskills: any;

  constructor(
    private gs: GigService,
    private js: JobService,
    private vs: VolService,
    private as: AdService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit() {
    if (this.route.snapshot.paramMap.get('slug')) {
      this.slug = this.route.snapshot.paramMap.get('slug');
    }
    this.countries.forEach((country, i) => {
      if (this.slug == country.slug) {
        this.country = country;
        this.match = true;
      }
    })
    if (this.country.code && this.country.code !== 'GL') {
      if (this.classification) {
        this.loadClassifieds();
      } else {
        if (this.showremote) {
          this.loadCountryRemoteAds();
        } else {
          this.loadCountryAds();
        }
      }
    } else {
      if (this.classification) {
        this.loadAllClassifieds();
      } else {
        this.loadAds();
      }
    }
    if (!this.match) {
      this.router.navigate(['/404']);
    }
  }

  loadAds() {
    if (this.showgigs) {
      this.gigsintown = this.gs.gigs();
    }
    if (this.showjobs) {
      this.jobsintown = this.js.jobs();
    }
    if (this.showvols) {
      this.volsintown = this.vs.vols();
    }
    if (this.showads) {
      this.adsintown  = this.as.ads();
    }
    this.loaded = true;
    this.scroll = true;
  }

  loadClassifieds() {
    if (this.showgigs) {
      const gigsclassified = this.gs.getClassifiedGigs(this.classification, this.country.code, this.fetch);
      const gigsalternate = this.gs.getClassifiedGigs(this.alternate_classification, this.country.code, this.fetch);
      const gigsextra = this.gs.getClassifiedGigs(this.extra_classification, this.country.code, this.fetch);
      const gigsgrouping = this.gs.getIndustryGigs(this.grouping, this.country.code, this.fetch);
      this.gigsintown = combineLatest([gigsclassified, gigsalternate, gigsextra, gigsgrouping]).pipe(
        map(arr => arr.reduce((acc, cur) => acc.concat(cur) ) ),
        map((allDocs: Gig[]) => {
          const auxObj = allDocs.reduce<{ [id: string]: Gig }>((acc, eachDoc) => {
            acc[eachDoc.id] = eachDoc;
            return acc;
          }, {});
          if (Object.keys(auxObj).length) {
            this.count = Object.keys(auxObj).length;
          }
          return Object.values(auxObj);
        })
      )
    }
    if (this.showjobs) {
      const jobsclassified = this.js.getClassifiedJobs(this.classification, this.country.code, this.fetch);
      const jobsalternate = this.js.getClassifiedJobs(this.alternate_classification, this.country.code, this.fetch);
      const jobsextra = this.js.getClassifiedJobs(this.extra_classification, this.country.code, this.fetch);
      const jobsgrouping = this.js.getIndustryJobs(this.grouping, this.country.code, this.fetch);
      this.jobsintown = combineLatest([jobsclassified, jobsalternate, jobsextra, jobsgrouping]).pipe(
        map(arr => arr.reduce((acc, cur) => acc.concat(cur) ) ),
        map((allDocs: Job[]) => {
          const auxObj = allDocs.reduce<{ [id: string]: Job }>((acc, eachDoc) => {
            acc[eachDoc.id] = eachDoc;
            return acc;
          }, {});
          if (Object.keys(auxObj).length) {
            this.count = Object.keys(auxObj).length;
          }
          return Object.values(auxObj);
        })
      )
    }
    if (this.showvols) {
      const volsclassified = this.vs.getClassifiedVols(this.classification, this.country.code, this.fetch);
      const volsalternate = this.vs.getClassifiedVols(this.alternate_classification, this.country.code, this.fetch);
      const volsextra = this.vs.getClassifiedVols(this.extra_classification, this.country.code, this.fetch);
      const volsgrouping = this.vs.getIndustryVols(this.grouping, this.country.code, this.fetch);
      this.volsintown = combineLatest([volsclassified, volsalternate, volsextra, volsgrouping]).pipe(
        map(arr => arr.reduce((acc, cur) => acc.concat(cur) ) ),
        map((allDocs: Vol[]) => {
          const auxObj = allDocs.reduce<{ [id: string]: Vol }>((acc, eachDoc) => {
            acc[eachDoc.id] = eachDoc;
            return acc;
          }, {});
          if (Object.keys(auxObj).length) {
            this.count = Object.keys(auxObj).length;
          }
          return Object.values(auxObj);
        })
      )
    }
    if (this.showads) {
      const adsclassified = this.as.getClassifiedAds(this.classification, this.country.code, this.fetch);
      const adsalternate = this.as.getClassifiedAds(this.alternate_classification, this.country.code, this.fetch);
      const adsextra = this.as.getClassifiedAds(this.extra_classification, this.country.code, this.fetch);
      const adsgrouping = this.as.getIndustryAds(this.grouping, this.country.code, this.fetch);
      this.adsintown = combineLatest([adsclassified, adsalternate, adsextra, adsgrouping]).pipe(
        map(arr => arr.reduce((acc, cur) => acc.concat(cur) ) ),
        map((allDocs: Ad[]) => {
          const auxObj = allDocs.reduce<{ [id: string]: Ad }>((acc, eachDoc) => {
            acc[eachDoc.id] = eachDoc;
            return acc;
          }, {});
          if (Object.keys(auxObj).length) {
            this.count = Object.keys(auxObj).length;
          }
          return Object.values(auxObj);
        })
      )
    }
    this.loaded = true;
  }

  loadAllClassifieds() {
    if (this.showgigs) {
      const gigsclassified = this.gs.getAllClassifiedGigs(this.classification, this.fetch);
      const gigsalternate = this.gs.getAllClassifiedGigs(this.alternate_classification, this.fetch);
      const gigsextra = this.gs.getAllClassifiedGigs(this.extra_classification, this.fetch);
      const gigsgrouping = this.gs.getAllIndustryGigs(this.grouping, this.fetch);
      this.gigsintown = combineLatest([gigsclassified, gigsalternate, gigsextra, gigsgrouping]).pipe(
        map(arr => arr.reduce((acc, cur) => acc.concat(cur) ) ),
        map((allDocs: Gig[]) => {
          const auxObj = allDocs.reduce<{ [id: string]: Gig }>((acc, eachDoc) => {
            acc[eachDoc.id] = eachDoc;
            return acc;
          }, {});
          if (Object.keys(auxObj).length) {
            this.count = Object.keys(auxObj).length;
          }
          return Object.values(auxObj);
        })
      )
    }
    if (this.showjobs) {
      const jobsclassified = this.js.getAllClassifiedJobs(this.classification, this.fetch);
      const jobsalternate = this.js.getAllClassifiedJobs(this.alternate_classification, this.fetch);
      const jobsextra = this.js.getAllClassifiedJobs(this.extra_classification, this.fetch);
      const jobsgrouping = this.js.getAllIndustryJobs(this.grouping, this.fetch);
      this.jobsintown = combineLatest([jobsclassified, jobsalternate, jobsextra, jobsgrouping]).pipe(
        map(arr => arr.reduce((acc, cur) => acc.concat(cur) ) ),
        map((allDocs: Job[]) => {
          const auxObj = allDocs.reduce<{ [id: string]: Job }>((acc, eachDoc) => {
            acc[eachDoc.id] = eachDoc;
            return acc;
          }, {});
          if (Object.keys(auxObj).length) {
            this.count = Object.keys(auxObj).length;
          }
          return Object.values(auxObj);
        })
      )
    }
    if (this.showvols) {
      const volsclassified = this.vs.getAllClassifiedVols(this.classification, this.fetch);
      const volsalternate = this.vs.getAllClassifiedVols(this.alternate_classification, this.fetch);
      const volsextra = this.vs.getAllClassifiedVols(this.extra_classification, this.fetch);
      const volsgrouping = this.vs.getAllIndustryVols(this.grouping, this.fetch);
      this.volsintown = combineLatest([volsclassified, volsalternate, volsextra, volsgrouping]).pipe(
        map(arr => arr.reduce((acc, cur) => acc.concat(cur) ) ),
        map((allDocs: Vol[]) => {
          const auxObj = allDocs.reduce<{ [id: string]: Vol }>((acc, eachDoc) => {
            acc[eachDoc.id] = eachDoc;
            return acc;
          }, {});
          if (Object.keys(auxObj).length) {
            this.count = Object.keys(auxObj).length;
          }
          return Object.values(auxObj);
        })
      )
    }
    if (this.showads) {
      const adsclassified = this.as.getAllClassifiedAds(this.classification, this.fetch);
      const adsalternate = this.as.getAllClassifiedAds(this.alternate_classification, this.fetch);
      const adsextra = this.as.getAllClassifiedAds(this.extra_classification, this.fetch);
      const adsgrouping = this.as.getAllIndustryAds(this.grouping, this.fetch);
      this.adsintown = combineLatest([adsclassified, adsalternate, adsextra, adsgrouping]).pipe(
        map(arr => arr.reduce((acc, cur) => acc.concat(cur) ) ),
        map((allDocs: Ad[]) => {
          const auxObj = allDocs.reduce<{ [id: string]: Ad }>((acc, eachDoc) => {
            acc[eachDoc.id] = eachDoc;
            return acc;
          }, {});
          if (Object.keys(auxObj).length) {
            this.count = Object.keys(auxObj).length;
          }
          return Object.values(auxObj);
        })
      )
    }
    this.loaded = true;
  }

  loadCountryAds() {
    if (this.showgigs) {
      this.gigsintown = this.gs.getCountryGigs(this.country.code, this.fetch);
    }
    if (this.showjobs) {
      this.jobsintown = this.js.getCountryJobs(this.country.code, this.fetch);
    }
    if (this.showvols) {
      this.volsintown = this.vs.getCountryVols(this.country.code, this.fetch);
    }
    if (this.showads) {
      this.adsintown  = this.as.getCountryAds(this.country.code, this.fetch);
    }
    this.loaded = true;
  }

  loadCountryRemoteAds() {
    if (this.showgigs) {
      const gigsonsite = this.gs.getCountryGigs(this.country.code, this.fetch);
      const gigsremote = this.gs.getRemoteGigs(this.fetch);
      this.gigsintown = combineLatest([gigsonsite, gigsremote]).pipe(
        map(arr => arr.reduce((acc, cur) => acc.concat(cur) ) ),
        map((allDocs: Gig[]) => {
          const auxObj = allDocs.reduce<{ [id: string]: Gig }>((acc, eachDoc) => {
            acc[eachDoc.id] = eachDoc;
            return acc;
          }, {});
          if (Object.keys(auxObj).length) {
            this.count = Object.keys(auxObj).length;
          }
          return Object.values(auxObj);
        })
      )
    }
    if (this.showjobs) {
      const jobsonsite = this.js.getCountryJobs(this.country.code, this.fetch);
      const jobsremote = this.js.getRemoteJobs(this.fetch);
      this.jobsintown = combineLatest([jobsonsite, jobsremote]).pipe(
        map(arr => arr.reduce((acc, cur) => acc.concat(cur) ) ),
        map((allDocs: Job[]) => {
          const auxObj = allDocs.reduce<{ [id: string]: Job }>((acc, eachDoc) => {
            acc[eachDoc.id] = eachDoc;
            return acc;
          }, {});
          if (Object.keys(auxObj).length) {
            this.count = Object.keys(auxObj).length;
          }
          return Object.values(auxObj);
        })
      )
    }
    if (this.showvols) {
      const volsonsite = this.vs.getCountryVols(this.country.code, this.fetch);
      const volsremote = this.vs.getRemoteVols(this.fetch);
      this.volsintown = combineLatest([volsonsite, volsremote]).pipe(
        map(arr => arr.reduce((acc, cur) => acc.concat(cur) ) ),
        map((allDocs: Vol[]) => {
          const auxObj = allDocs.reduce<{ [id: string]: Vol }>((acc, eachDoc) => {
            acc[eachDoc.id] = eachDoc;
            return acc;
          }, {});
          if (Object.keys(auxObj).length) {
            this.count = Object.keys(auxObj).length;
          }
          return Object.values(auxObj);
        })
      )
    }
    if (this.showads) {
      const adsonsite  = this.as.getCountryAds(this.country.code, this.fetch);
      const adsremote  = this.as.getRemoteAds(this.fetch);
      this.adsintown = combineLatest([adsonsite, adsremote]).pipe(
        map(arr => arr.reduce((acc, cur) => acc.concat(cur) ) ),
        map((allDocs: Ad[]) => {
          const auxObj = allDocs.reduce<{ [id: string]: Ad }>((acc, eachDoc) => {
            acc[eachDoc.id] = eachDoc;
            return acc;
          }, {});
          if (Object.keys(auxObj).length) {
            this.count = Object.keys(auxObj).length;
          }
          return Object.values(auxObj);
        })
      )
    }
    this.loaded = true;
  }

  show(service: string) {
    this.showgigs = false;
    this.showjobs = false;
    this.showvols = false;
    this.showads = false;
    if (service == 'gigs') {
      this.showgigs = true;
    }
    if (service == 'jobs') {
      this.showjobs = true;
    }
    if (service == 'vols') {
      this.showvols = true;
    }
    if (service == 'ads') {
      this.showads = true;
    }
  }
}
